import { useEffect } from "react";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import update from "immutability-helper";
import { Autocomplete, Fade, FormControl } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { replaceRobotActionList, selectActiveRobotVersion } from "../../../store/ses/sesRobotSlice";
import { selectChannelList } from "../../../store/langSlice";
import useAccessRight from "../../../hooks/useAccessRight";
import useTranslate from "../../../hooks/useTranslate";
import { ROBOT_EVENT_LIST } from "../../../constants/robotConfigLists";
import { SES } from "../../../constants/accessRights";
import { RobotActionEventType } from "../../../types/ses/sesRobotTypes";
import ActionEvent from "../../ActionEvent/ActionEvent";
import FormAddingAction from "../../Forms/FormAddingAction/FormAddingAction";
import NoOptionText from "../../../ui/NoOptionText/NoOptionText";
import { IEventsTabProps } from "./EventsTab.props";
import styles from "./EventsTab.module.scss";
import { useFormik } from "formik";
import { RenderOption, TextfieldWithAutocomplete } from "@/ui";
import { useSearchActionsDataElemByConditionsAndCategoties } from "@/hooks";
import { selectSesHints } from "@/store/hintsSlice";
import { CustomMenuItem, CustomTextField } from "@/ui";

const EventsTab = ({
	robotData,
	changeFlg,
	changeFlgHandler,
	selectEvent,
	setSelectEvent,
	inputChannel,
	setInputChannel,
	editRobotHandler,
}: IEventsTabProps): JSX.Element => {
	const dispatch = useAppDispatch();
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const channelList = useAppSelector(selectChannelList); // store - список каналов
	const sesHints = useAppSelector(selectSesHints);

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	const formik = useFormik({
		initialValues: { searchByDataElem: "" },
		validateOnMount: false,
		validateOnChange: false,
		validateOnBlur: true,
		onSubmit: () => {},
	});

	// следим за каналом
	useEffect(() => {
		// меняем определенные события при смене канала voice
		if (selectEvent === "interrupt") {
			inputChannel !== "voice" && setSelectEvent("start");
		}
		if (selectEvent === "answer delay") {
			inputChannel !== "voice" && inputChannel !== "web" && setSelectEvent("start");
		}
		// if (visibleBlockConfig === "events") {
		// если есть событие и в нем отсутствует выбранный канал - выбираем первый из возможных
		// if (robotData.actions[selectEvent] && !robotData.actions[selectEvent][inputChannel]) {
		// 	setInputChannel(Object.keys(robotData.actions[selectEvent])[0]);
		// }
		// если нет события - выбираем default канал
		// else if (!robotData.actions[selectEvent]) {
		// 	setInputChannel("default");
		// }
		// }
	}, [/*selectEvent,  visibleBlockConfig */ inputChannel]);

	// обработчик перемещения действий
	const moveAction = (dragIndex: number, hoverIndex: number) => {
		dispatch(
			replaceRobotActionList({
				actionEvent: selectEvent,
				channel: inputChannel,
				actionList: update(robotData.actions[selectEvent][inputChannel], {
					$splice: [
						[dragIndex, 1],
						[hoverIndex, 0, robotData.actions[selectEvent][inputChannel][dragIndex]],
					],
				}),
			})
		); // изменение последовательности действий
		changeFlgHandler("actions"); // ставим флаг о несохраненных данных
	};

	const { dataElemsAutocompleteOptions, visibleActionsIds, dataElemIn } = useSearchActionsDataElemByConditionsAndCategoties({
		data: robotData?.actions && Object.entries(robotData.actions).length > 0 ? robotData.actions[selectEvent]?.[inputChannel] ?? [] : [],
		searchInputValue: formik.values.searchByDataElem,
		setSearchInputValue: (values: { searchByDataElem: string }) => {
			formik.setValues(values);
		},
	});

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.events}>
				<div className={styles.eventsBlock}>
					{/* канал */}
					<Autocomplete
						fullWidth
						freeSolo={isAccess(SES.ROBOT_EDIT) && activeRobotVersion === "draft"}
						options={Array.from(
							new Set(
								Object.keys(robotData.actions[selectEvent] || {}).concat(
									channelList.data.map((channel) => channel.name),
									"default"
								)
							)
						)}
						value={inputChannel}
						onChange={(_, value) => setInputChannel(value ? value : "")}
						noOptionsText={<NoOptionText />}
						renderInput={(params) => (
							<CustomTextField
								{...params}
								label={translate("input_channel")}
								onChange={(e) => isAccess(SES.ROBOT_EDIT) && setInputChannel(e.target.value)}
								hint={sesHints.hint_sesTabChannels}
							/>
						)}
						renderOption={(props, option) => {
							return (
								<RenderOption {...props} key={option}>
									{option}
								</RenderOption>
							);
						}}
					/>

					{/* событие */}
					<CustomTextField
						select
						fullWidth
						label={translate("select_type")}
						value={selectEvent}
						onChange={(e) => setSelectEvent(e.target.value as RobotActionEventType)}
						hint={sesHints.hint_sesBlockEventTypes}
						hintPosition="left"
					>
						{ROBOT_EVENT_LIST.map(({ event, translation }) => (
							<CustomMenuItem
								key={event}
								value={event}
								disabled={(event === "interrupt" && inputChannel !== "voice") || (event === "answer delay" && inputChannel !== "voice" && inputChannel !== "web")}
								// hint={`hint_eventsTab${event.charAt(0).toUpperCase() + event.slice(1).replace(" ", "")}Type`}
								// hintPosition="left"
							>
								{translate(translation)} {robotData.actions[event]?.[inputChannel]?.length > 0 && `(${robotData.actions[event]?.[inputChannel].length})`}
							</CustomMenuItem>
						))}
					</CustomTextField>
				</div>
				<FormControl sx={{ m: "0 16px" }}>
					<TextfieldWithAutocomplete
						autocompleteOptionsWithId={dataElemsAutocompleteOptions}
						value={formik.values.searchByDataElem} // здесь id выбранной КТ
						setValues={formik.setValues}
						values={formik.values}
						onChange={(e) => formik.handleChange(e)}
						name="searchByDataElem"
						label={translate("input_filterByDataElements")}
					/>
				</FormControl>
				{/* действия */}
				<div className={styles.actions}>
					<DndProvider backend={HTML5Backend}>
						{robotData.actions[selectEvent]?.[inputChannel]?.map((action, idx, actionsArr) => (
							<ActionEvent
								key={action.actionId || JSON.stringify(action)}
								action={action}
								idx={idx}
								actionsArr={actionsArr}
								channel={inputChannel}
								changeFlgHandler={changeFlgHandler}
								isFilterActive={formik.values.searchByDataElem !== ""}
								searchedDataElemIn={dataElemIn}
								isActionVisible={
									visibleActionsIds.length !== 0
										? action.actionId
											? visibleActionsIds.includes(action.actionId)
											: visibleActionsIds.includes(`${action.action}-${idx}`)
										: true
								}
								actionFor={{
									for: "robotEvent",
									event: selectEvent,
									isShownSaveButton: true,
									isAccessSaveButton: isAccess(SES.ROBOT_EDIT),
									disabledSaveButton: !changeFlg.thisIs,
									onClickSaveButtonHandler: editRobotHandler,
								}}
								moveAction={moveAction}
							/>
						))}
					</DndProvider>

					{/* доп.ограничение по типу события + канал */}
					{isAccess(SES.ROBOT_EDIT) &&
						activeRobotVersion === "draft" &&
						!(
							(selectEvent === "answer delay" || selectEvent === "interrupt") &&
							(inputChannel === "voice" || inputChannel === "web") &&
							robotData.actions[selectEvent]?.[inputChannel]?.length > 0
						) && (
							<FormAddingAction
								channel={inputChannel}
								changeFlgHandler={changeFlgHandler}
								actionFor={{
									for: "robotEvent",
									event: selectEvent,
									isShownSaveButton: true,
									isAccessSaveButton: isAccess(SES.ROBOT_EDIT),
									disabledSaveButton: !changeFlg.thisIs,
									onClickSaveButtonHandler: editRobotHandler,
								}}
							/>
						)}
				</div>
			</div>
		</Fade>
	);
};

export default EventsTab;
